import sortBy from 'lodash/sortBy'
import moment from 'moment'

import type Company from 'modules/core/store/models/Company'

import { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import type IncidenceChange from 'modules/bookkeepers/store/models/Change'
import { EMPLOYEE_TRACKED_CHANGES } from 'modules/bookkeepers/lib/constants'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type { AvailableChange, IncidenceChangeItem } from 'modules/payroll_bundle/lib/types'

import { CHANGE_CREATED, getAvailableChanges, getModifiedValue, parseChange } from './changesHelper'

const COST_CENTER_FIELDS = new Set(['cost_center', 'percentage'])
const COST_CENTER_CHANGE_IDS = new Set(['cost_center_updated', 'cost_center_percentage_updated'])

const processIncidenceChanges = (
  currentChange: IncidenceChange,
  previousChange: IncidenceChange | undefined,
  incidenceType: string,
  company: Company,
  trackedFields?: Set<string>
) => {
  const changes: IncidenceChangeItem[] = []
  const availableChanges = getAvailableChanges(incidenceType)

  availableChanges.forEach((changeType: AvailableChange) => {
    if (trackedFields && !trackedFields.has(changeType.field)) {
      return
    }

    const currentValue = getModifiedValue(
      changeType.field,
      currentChange.attributes,
      company,
      changeType.value_modifier
    )

    const previousValue = getModifiedValue(
      changeType.field,
      previousChange?.attributes,
      company,
      changeType.value_modifier
    )

    if (
      !EMPLOYEE_TRACKED_CHANGES[incidenceType] ||
      EMPLOYEE_TRACKED_CHANGES[incidenceType]?.includes(changeType.field)
    ) {
      const processedChange = parseChange(
        currentValue,
        previousValue,
        changeType,
        currentChange.attributes.effective_at,
        currentChange.attributes.author
      )

      if (processedChange) {
        changes.push(processedChange)
      }
    }
  })

  return changes
}

const getCostCenterKey = (incidenceChange: IncidenceChange) => {
  const rawCostCenterId = incidenceChange.attributes.cost_center_id
  const rawCostCenterName = incidenceChange.attributes.cost_center

  let costCenterId: number | null = null

  if (rawCostCenterId != null && rawCostCenterId !== '') {
    const parsedId = Number(rawCostCenterId)

    if (Number.isInteger(parsedId) && parsedId > 0) {
      costCenterId = parsedId
    }
  }

  if (costCenterId !== null) {
    return `id:${costCenterId}`
  }

  if (typeof rawCostCenterName === 'string' && rawCostCenterName !== '') {
    return `name:${rawCostCenterName}`
  }

  return null
}

const sortByEffective = (collection: IncidenceChange[]) => {
  return sortBy(collection, (change) => change.get('effective_at'))
}

const isAffectedContract = (model: IncidenceChange, contractId: string | null) => {
  return (
    model.has('original_contract_version_id') &&
    model.get('original_contract_version_id') === contractId
  )
}

const sortChanges = (
  incidenceChanges: IncidenceChange[],
  incidenceType: string,
  incidenceTargetId: string | null
) => {
  if (incidenceType === INCIDENCE_TYPES.contract) {
    // Because the way Contract Versions work, we need the previous version and the current one. However, when
    // a change in a previous contract has happened, this can cause problems
    // For that reason, we sort it in two ways, first the affected contract (which is the one from the incidence)
    // and then the support contract, which is the contract with return that has no direct relation with the actual
    // contract
    let affectedContractChanges = incidenceChanges.filter((model) =>
      isAffectedContract(model, incidenceTargetId)
    )
    affectedContractChanges = sortByEffective(affectedContractChanges)

    let supportContractChange = incidenceChanges.filter(
      (model) => !isAffectedContract(model, incidenceTargetId)
    )
    supportContractChange = sortByEffective(supportContractChange)

    return [...supportContractChange, ...affectedContractChanges]
  }

  return sortByEffective(incidenceChanges)
}

const buildIncidenceChanges = (
  incidenceChanges: IncidenceChange[],
  incidenceType: string,
  company: Company,
  incidenceTargetId: string | null,
  incidence: IncidenceQueryData
) => {
  const sortedChanges = sortChanges(incidenceChanges, incidenceType, incidenceTargetId)

  if (incidenceType === INCIDENCE_TYPES.cost_center) {
    const previousChangesByCostCenterId = new Map<string, IncidenceChange>()
    const createdChange: IncidenceChangeItem = {
      id: CHANGE_CREATED,
      effectiveAt: moment(incidence.createdAt),
    }

    const costCenterChanges = sortedChanges.flatMap((incidenceChange: IncidenceChange) => {
      const costCenterKey = getCostCenterKey(incidenceChange)
      const previousChange =
        costCenterKey === null ? undefined : previousChangesByCostCenterId.get(costCenterKey)

      const changes = processIncidenceChanges(
        incidenceChange,
        previousChange,
        incidenceType,
        company
      )

      if (costCenterKey !== null) {
        previousChangesByCostCenterId.set(costCenterKey, incidenceChange)
      }

      return changes
    })

    return [createdChange, ...costCenterChanges]
  }

  if (incidenceType === INCIDENCE_TYPES.hiring) {
    const previousChangesByCostCenterId = new Map<string, IncidenceChange>()

    return sortedChanges.flatMap((incidenceChange: IncidenceChange, index: number) => {
      const costCenterKey = getCostCenterKey(incidenceChange)

      if (index === 0) {
        const initialCostCenterChanges =
          costCenterKey === null
            ? []
            : processIncidenceChanges(
                incidenceChange,
                undefined,
                incidenceType,
                company,
                COST_CENTER_FIELDS
              )

        if (costCenterKey !== null) {
          previousChangesByCostCenterId.set(costCenterKey, incidenceChange)
        }

        return [
          {
            id: CHANGE_CREATED,
            effectiveAt: moment(incidence.createdAt),
          },
          ...initialCostCenterChanges,
        ]
      }

      const previousSequentialChange = sortedChanges[index - 1]

      const standardChanges = processIncidenceChanges(
        incidenceChange,
        previousSequentialChange,
        incidenceType,
        company
      )

      if (costCenterKey === null) {
        return standardChanges
      }

      const previousCostCenterChange = previousChangesByCostCenterId.get(costCenterKey)

      const costCenterChanges = processIncidenceChanges(
        incidenceChange,
        previousCostCenterChange,
        incidenceType,
        company,
        COST_CENTER_FIELDS
      )

      previousChangesByCostCenterId.set(costCenterKey, incidenceChange)

      return [
        ...standardChanges.filter((change) => !COST_CENTER_CHANGE_IDS.has(change.id)),
        ...costCenterChanges,
      ]
    })
  }

  return sortedChanges.flatMap((incidenceChange: IncidenceChange, index: number) => {
    if (index === 0) {
      const firstChangeId = CHANGE_CREATED

      const change: IncidenceChangeItem = {
        id: firstChangeId,
        effectiveAt: moment(incidence.createdAt),
      }

      return change
    }

    return processIncidenceChanges(
      incidenceChange,
      sortedChanges[index - 1],
      incidenceType,
      company
    )
  })
}

export default buildIncidenceChanges
